import { Icon } from "@circle-react-shared/Icon";
import { useTipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { scrollToBottom } from "@circle-react-uikit/TipTap/utilities";
import { HtmlVideoPlayer } from "@circle-react-uikit/VideoPlayer/HtmlVideoPlayer";
import { useAttachmentGallery } from "@circle-react/components/Modals/AttachmentGalleryModal";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import {
  activeStorageBlobUrl,
  directUploadPath,
} from "@circle-react/helpers/urlHelpers";
import { Typography } from "@circleco/compass/components/Typography";
import { DirectUpload } from "@rails/activestorage";
import { useCallback, useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { WaveSurferAudioPlayer } from "@/react/components/shared/uikit/TipTap/VoiceMessage/WaveSurferAudioPlayer";
import { useScrollInfo } from "@/react/hooks/chatsV2/useScrollInfo";

import { AttachmentDetails } from "../AttachmentPreviews/AttachmentDetails";
import { useAttachment } from "../AttachmentPreviews/useAttachment";
import { CancelButtonWithProgress } from "./CancelButtonWithProgress";
import { DownloadButton } from "./DownloadButton";
import type { Attachment, AttachmentPreview } from "./types";

export interface AttachmentItemProps {
  attachment: Attachment;
  attachmentsForGalleryPreview: AttachmentPreview[];
  index: number;
  shouldShowEnlarged: boolean;
}

export const AttachmentItem = ({
  attachmentsForGalleryPreview,
  attachment,
  index,
  shouldShowEnlarged,
}: AttachmentItemProps) => {
  const {
    disableAttachmentClicks = false,
    editor,
    setLocalAttachments,
    shouldScrollIntoView,
  } = useTipTapEditorContext();
  const { show: openAttachmentGallery } = useAttachmentGallery();
  const { scrollerId } = useScrollInfo();
  const [progress, setProgress] = useState(0);
  const [isImgLoaded, setIsImgLoaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const queuedForCancellation = useRef(false);
  const imageVariants = attachment.image_variants || {};
  const removeCurrentAttachment = () => {
    setLocalAttachments((currentValue: any) =>
      currentValue.filter((_: any, i: any) => i !== index),
    );
  };

  const updateCurrentAttachment = useCallback(
    (newValue: any) => {
      setLocalAttachments((current: any) => {
        const newAttachments = [...current];
        newAttachments[index] = newValue;
        return newAttachments;
      });
    },
    [index, setLocalAttachments],
  );

  const remoteURLToFile = async ({ url, filename, content_type }: any) => {
    const response = await fetch(url);
    return new File([await response.blob()], filename, {
      type: content_type,
    });
  };

  const handleUpload = useCallback(async () => {
    setIsUploading(true);
    const file =
      attachment.file ||
      (await remoteURLToFile({
        url: attachment.url,
        filename: `${attachment.id}.${attachment.content_type.split("/")[1]}`,
        content_type: attachment.content_type,
      }));
    const upload = new DirectUpload(file, directUploadPath(), {
      directUploadWillStoreFileWithXHR: xhr =>
        xhr.upload.addEventListener("progress", e => {
          if (queuedForCancellation.current) {
            xhr.abort();
            setIsUploading(false);
          }
          setProgress((e.loaded / e.total) * 100);
        }),
    });
    upload.create((error, blob) => {
      if (error) {
        throw new Error(`Direct upload failed: ${String(error)}`);
      } else {
        const url = activeStorageBlobUrl({
          signed_id: blob.signed_id,
          filename: blob.filename,
        });
        updateCurrentAttachment({
          ...blob,
          url,
          uploaded: true,
        });
        setIsUploading(false);
      }
    });
  }, [attachment, updateCurrentAttachment]);

  useEffect(() => {
    if (attachment.toUpload && !isUploading) {
      void handleUpload();
    }
  }, [attachment, isUploading, handleUpload]);

  const {
    isImage,
    isVoiceMessage,
    isVideo,
    doesAttachmentSupportGalleryPreview,
    isImageOrVideo,
    isPDF,
  } = useAttachment(attachment);

  let imagePreviewURL = attachment.url;
  if (!editor.isEditable) {
    if (shouldShowEnlarged) {
      imagePreviewURL = imageVariants.small ?? attachment.url;
    } else {
      imagePreviewURL = imageVariants.thumbnail ?? attachment.url;
    }
  }

  imagePreviewURL = imagePreviewURL ?? attachment?.url;

  const imageEventListeners = {
    onLoad: () => {
      setIsImgLoaded(true);
      if (shouldScrollIntoView) {
        scrollToBottom(scrollerId);
      }
    },
    onError: () => {
      setIsImgLoaded(true);
    },
  };

  if (isVoiceMessage) {
    return (
      <div className="group flex w-[286px] sm:w-[317px] min-w-0 relative">
        {editor.isEditable && (
          <CancelButtonWithProgress
            isUploading={isUploading}
            progress={progress}
            queuedForCancellation={queuedForCancellation}
            removeCurrentAttachment={removeCurrentAttachment}
          />
        )}
        <WaveSurferAudioPlayer
          src={attachment.url}
          isVoiceMessage={isVoiceMessage}
          transcriptionId={attachment.metadata?.transcript_id}
          transcriptionPreview={attachment.metadata?.transcript_preview}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() =>
        !disableAttachmentClicks &&
        !editor.isEditable &&
        doesAttachmentSupportGalleryPreview &&
        openAttachmentGallery({
          attachments: attachmentsForGalleryPreview,
          startIndex: attachmentsForGalleryPreview.findIndex(
            a => a.url == attachment.url,
          ),
        })
      }
      className={classNames(
        "group relative flex w-[280px] max-w-full border-primary shadow-xs gap-2 bg-primary rounded-lg border hover:border-hover hover:bg-hover",
        disableAttachmentClicks ? "cursor-default" : "cursor-pointer",
        {
          "h-[60px] px-4 py-2 w-[280px] max-w-full": !isImageOrVideo,
          "h-[60px] w-[60px]": isImageOrVideo && !shouldShowEnlarged,
          "h-[200px] w-auto min-w-[150px] !max-w-[264px]":
            isImageOrVideo && shouldShowEnlarged,
        },
      )}
    >
      {editor.isEditable && (
        <CancelButtonWithProgress
          isUploading={isUploading}
          progress={progress}
          queuedForCancellation={queuedForCancellation}
          removeCurrentAttachment={removeCurrentAttachment}
        />
      )}
      {isVideo && (
        <>
          <HtmlVideoPlayer
            src={attachment.url}
            type={attachment.content_type}
            poster={attachment?.image_variants?.thumbnail}
            className="h-full rounded-lg border object-cover"
            controls={false}
            preload="none"
          />
          {shouldShowEnlarged ? (
            <div className="absolute bottom-2 left-2 flex items-center rounded-sm bg-black/50 py-0.5 pr-2">
              <Icon type="12-play" size={12} className="size-4 text-white" />
              <Typography variant="caption" color="inverse">
                {t("tiptap.video")}
              </Typography>
            </div>
          ) : (
            <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center rounded-sm bg-black/50 px-0.5 py-1">
              <Icon type="12-play" size={12} className="text-white" />
            </div>
          )}
        </>
      )}
      {!doesAttachmentSupportGalleryPreview && !editor.isEditable && (
        <DownloadButton url={attachment.url} />
      )}
      {isImage && (
        <img
          loading="eager"
          alt={t("tiptap.attachment_previews.image")}
          className={classNames(
            "rounded-lg object-cover",
            isImgLoaded ? "size-full" : "hidden",
          )}
          src={imagePreviewURL}
          {...imageEventListeners}
        />
      )}
      {(!doesAttachmentSupportGalleryPreview || isPDF) && (
        <AttachmentDetails attachment={attachment} />
      )}
    </button>
  );
};
